//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { getContentDetailFilterDateRange } from '@apis/content-detail/contentDetailApiModels';
import { DateRangeAlias } from '@components/date-range-picker/dateRangePickerModels';
import {
  COMMENTS_TABLE_HEADER,
  COMMENTS_TOOLTIP,
  ENGAGEMENT_TABLE_HEADER,
  ENGAGEMENT_TOOLTIP,
  LIKES_TABLE_HEADER,
  LIKES_TOOLTIP,
  POSTED_TIMESTAMP_TABLE_HEADER,
  REACH_TABLE_HEADER,
  REACH_TOOLTIP,
  SHARE_COUNT_TABLE_HEADER,
  SHARE_COUNT_TOOLTIP,
  VIEWS_TABLE_HEADER,
  VIEWS_TOOLTIP,
} from '@instagram-account/components/common/instagramConstants';
import DateString from '@models/DateString';
import { SocialNetwork } from '@models/SocialNetwork';
import TimeZoneId from '@models/TimeZoneId';
import { getReachTooltipContentCaveat } from '@utils/social-text-utils/socialTextUtils';

export const instagramSummaryStatBubbleTitles = {
  Followers: 'Change in Followers',
  Reach: 'Account Reach',
  EngagementRate: 'Total Eng. Rate',
  EngagementRateFull: 'Total Engagement Rate',
  Revenue: 'Revenue',
  Visits: 'Site Visits',
  Transactions: 'Transactions',
};

export const instagramSummaryStatBubbleTooltips = {
  Followers: 'Net change in follower count over the last 7 days.',
  Reach: `Unique users who have viewed any of your media over the last 7 days. This includes views on Brand posts as well as Ads and Promoted posts. ${getReachTooltipContentCaveat(
    SocialNetwork.Instagram,
  )}`,
  EngagementRate:
    'Total likes, comments, saves, and shares divided by reach for all your feed posts over the last 7 days. This metric only includes organic traffic.',
  Revenue:
    'Revenue generated from promoted posts, links in your story, and your Like2Buy link in bio.',
  Visits:
    'Site visits you received from your Instagram channel links in the last 7 days.',
  Transactions:
    'Transactions that occurred during site visits generated by your Instagram channel links.',
};

export const RECENT_POSTS_TABLE_COUNT = 6;
export const RECENT_POSTS_TABLE_METRICS_COLUMN_COUNT = 7;

export const RECENT_POSTS_TABLE_FEED_ENGAGEMENT_GA_TRACKING_LABEL =
  'RecentPostPerformance';

export const RECENT_POSTS_TABLE_TOOLTIP = `View the performance for your ${RECENT_POSTS_TABLE_COUNT} most recent posts. To see more post engagement data, click the "View Engagement" button.`;

export enum EngagementMetric {
  PostedDate = 'POSTED_DATE',
  EngagementRate = 'ENGAGEMENT_RATE',
  Reach = 'REACH',
  Likes = 'LIKES',
  Comments = 'COMMENTS',
  ShareCount = 'SHARE_COUNT',
  Views = 'VIEWS',
}

export const recentPostPerformanceTableHeaderTitles: Record<
  EngagementMetric,
  string
> = {
  [EngagementMetric.PostedDate]: POSTED_TIMESTAMP_TABLE_HEADER,
  [EngagementMetric.Reach]: REACH_TABLE_HEADER,
  [EngagementMetric.Likes]: LIKES_TABLE_HEADER,
  [EngagementMetric.Comments]: COMMENTS_TABLE_HEADER,
  [EngagementMetric.ShareCount]: SHARE_COUNT_TABLE_HEADER,
  [EngagementMetric.EngagementRate]: ENGAGEMENT_TABLE_HEADER,
  [EngagementMetric.Views]: VIEWS_TABLE_HEADER,
} as const;

export const recentPostPerformanceTableHeaderTooltips: Omit<
  Record<EngagementMetric, { tooltip: string; learnMoreLink?: string }>,
  'POSTED_DATE'
> = {
  [EngagementMetric.Reach]: {
    tooltip: REACH_TOOLTIP,
  },
  [EngagementMetric.Likes]: {
    tooltip: LIKES_TOOLTIP,
  },
  [EngagementMetric.Comments]: {
    tooltip: COMMENTS_TOOLTIP,
  },
  [EngagementMetric.ShareCount]: {
    tooltip: SHARE_COUNT_TOOLTIP,
  },
  [EngagementMetric.EngagementRate]: {
    tooltip: ENGAGEMENT_TOOLTIP,
  },
  [EngagementMetric.Views]: {
    tooltip: VIEWS_TOOLTIP,
  },
} as const;

export const postPerformanceTableHeaders: readonly EngagementMetric[] = [
  EngagementMetric.PostedDate,
  EngagementMetric.Views,
  EngagementMetric.Reach,
  EngagementMetric.Likes,
  EngagementMetric.Comments,
  EngagementMetric.ShareCount,
  EngagementMetric.EngagementRate,
] as const;

export const COPYRIGHTED_PLACEHOLDER_TYPE = 'condensed';

export const ACCOUNT_SUMMARY_LEARN_MORE_URL =
  'https://support.bazaarvoice.com/s/article/Instagram-Summary-Page-Overview';
export const ACCOUNT_SUMMARY_SIDEBAR_COLUMN_WIDTH = 240;

export const DEFAULT_LAST_WEEK_DATE_RANGE = {
  endDate: DateString.getToday(TimeZoneId.AmericaLosAngeles),
  startDate: DateString.addDays(
    DateString.getToday(TimeZoneId.AmericaLosAngeles),
    -6,
  ),
} as const;

export const DEFAULT_90_DAY_CONTENT_DETAIL_FILTER_DATE_RANGE =
  getContentDetailFilterDateRange(
    moment().subtract(90, 'd').toDate(),
    new Date(),
  );

export const MENTIONED_TAGGED_POSTS_LIMIT = 24;
export const SUMMARY_TOP_RECENT_ENGAGERS_LIMIT = 12;

export const INSTAGRAM_ANALYTICS_DATE_RANGE_ALIASES: DateRangeAlias[] = [
  'last7Days',
  'last30Days',
  'last3Months',
];
